import { useAccountQuery, useCreateAccountMutation, useUpdateAccountMutation, useDeleteAccountMutation } from './useAccountsQuery';
import { Account } from '../types';
import { saveCustomer, reassignAccountCustomer } from "../services/BillingService/BillingService.ts";

export const useAccount = (accountId?: string) => {
    const { data: record, isLoading: loading, refetch: fetchRecord } = useAccountQuery(accountId || '');
    const createAccountMutation = useCreateAccountMutation();
    const updateAccountMutation = useUpdateAccountMutation();
    const deleteAccountMutation = useDeleteAccountMutation();

    const createRecord = async (values: Account) => {
        return await createAccountMutation.mutateAsync(values);
    };

    const updateRecord = async (values: Account) => {
        if (typeof accountId === 'undefined') {
            throw new Error('accountId is required parameter');
        }
        return await updateAccountMutation.mutateAsync({ id: accountId, record: values });
    };

    const deleteRecord = async () => {
        if (typeof accountId === 'undefined') {
            throw new Error('accountId is required parameter');
        }
        return await deleteAccountMutation.mutateAsync(accountId);
    };

    const createCustomer = async () => {
        if (typeof accountId === 'undefined') {
            throw new Error('accountId is required parameter');
        }

        if (!record) {
            return;
        }

        const response = await saveCustomer(record as Account);

        if (!response) {
            throw new Error('Failed to create billing account');
        }

        return await updateAccountMutation.mutateAsync({
            id: accountId,
            record: {
                ...record,
                customerId: response.id
            } as Account
        });
    };

    // FNP-575: re-assign which Customer an account owns, via setAccountCustomerId
    // (the single writer). Used by the Billing tab's admin re-assign action.
    const reassignCustomer = async (targetAccountId: string, customerId: string) => {
        return await reassignAccountCustomer(targetAccountId, customerId);
    };

    return {
        record,
        loading,
        fetchRecord,
        createRecord,
        updateRecord,
        deleteRecord,
        createCustomer,
        reassignCustomer
    };
};
